// One decision waiting on a person, with its verbs: Accept · Edit then accept · Reject with reason (and feedback to the agent) ·
// Reply with guidance · Snooze. Used by the Inbox (with its keys), Home's proposals and a workpaper's Answers tab, so a proposal
// is decided the same way everywhere. An agent's proposal is shown as the engine published it (before/after per item, with its
// sources); the form opens with the engine's payload template (known fields fixed, what to fill with its hints, required marks).
// Every verb is one catalogue command through the inbox; reasons, edits and feedback are the person's own words.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AgentChip, Button, Card, DiffView, ErrorState, Kbd, ProposalView, SchemaForm, SourceList } from "../components";
import { newIdempotencyKey } from "../data/source";
import type { DecisionItem } from "../data/types";
import { mayRequest, validatePayload, withToFill } from "../lib/commands";
import { feedbackTarget } from "../lib/decisions";
import { titleCase } from "../lib/format";
import { useApp } from "./context";
import { useEngine } from "./engine";

export type DecisionMode = null | "edit" | "reject" | "guidance";
const ID_FIELDS = new Set(["id", "ids", "key", "workpaper", "decision", "target", "target_kind", "thread", "entry", "topic", "answers", "finding", "item", "role"]);

export function DecisionPanel({ d, mode: controlled, setMode: setControlled, nudge, onSnooze, onUnsnooze, snoozedView, onSent, keys = false, title }: {
  d: DecisionItem; mode?: DecisionMode; setMode?: (m: DecisionMode) => void; nudge?: number; onSnooze?: () => void; onUnsnooze?: () => void;
  snoozedView?: boolean; onSent?: (status: string) => void; keys?: boolean; title?: ReactNode;
}) {
  const { catalogue, me } = useApp();
  const request = useEngine();
  const [key] = useState(newIdempotencyKey);
  const [note, setNote] = useState<string>();
  const [own, setOwn] = useState<DecisionMode>(null);
  const mode = controlled !== undefined ? controlled : own;
  const setMode = setControlled ?? setOwn;
  const acc = d.decision.accept ?? null;
  const rej = d.decision.reject ?? null;
  const gui = d.decision.guidance ?? null;
  const scope = d.engagementId ? "engagement" : "firm";
  const reason = (cmd: string | undefined) => { const a = mayRequest(cmd ? catalogue.get(cmd) : undefined, me, scope); return a.ok ? null : a.reason; };
  const accBlocked = acc ? reason(acc.command) : "No engine command accepts this.";
  const rejBlocked = rej ? reason(rej.command) : "No engine command rejects this; reply with guidance instead.";
  const guiBlocked = gui ? reason(gui.command) : "No guidance command for this item.";
  // The engine's to_fill applies to the action's own command (the accept verb when it is the same command).
  const toFill = acc && acc.command === d.command ? d.toFill : [];
  const fb = feedbackTarget(d);
  const fbBlocked = fb ? reason("feedback.add") : null;
  const [feedback, setFeedback] = useState<{ on: boolean; rating: "needs_work" | "wrong" }>({ on: false, rating: "wrong" });

  const send = async (command: string, payload: Record<string, unknown>, what: string, k = `${key}:${command}`) => {
    const row = await request({ command, engagementId: d.engagementId, payload, what, idempotencyKey: k });
    if (row) onSent?.(row.status);
    return row;
  };
  const accept = async () => {
    if (!acc || accBlocked) return;
    const spec = withToFill(catalogue.get(acc.command)!, toFill);
    if (Object.keys(validatePayload(spec, acc.payload)).length) { setNote("The engine needs more from you before it can accept this."); setMode("edit"); return; }
    await send(acc.command, acc.payload, `Accept: ${d.what}`);
  };
  const first = useRef(true);
  useEffect(() => { if (first.current) { first.current = false; return; } void accept(); }, [nudge]); // eslint-disable-line react-hooks/exhaustive-deps

  const fixedOf = (p: Record<string, unknown>) => Object.fromEntries(Object.entries(p).filter(([k]) => ID_FIELDS.has(k)));
  const k = (x: string) => (keys ? <> <Kbd>{x}</Kbd></> : null);
  return (
    <Card title={title ?? <span className="flex flex-wrap items-center gap-2">{titleCase(d.kind)}{d.byAgent && <AgentChip />}</span>}
      action={d.href ? <Link to={d.href} className="text-xs text-brand hover:underline">Open in context</Link> : undefined}>
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-semibold">{d.what}</h2>
          <p className="text-sm text-muted">{d.engagementName ?? "Firm"}{d.who ? ` · for ${d.who}` : ""}</p>
          {d.why && <p className="mt-2 text-sm">{d.why}</p>}
        </div>
        {d.proposal ? <ProposalView proposal={d.proposal} /> : (d.decision.before || d.decision.after) && <DiffView before={d.decision.before} after={d.decision.after} />}
        {(d.sources.length > 0 || (d.decision.sources?.length ?? 0) > 0) && (
          <div className="flex flex-wrap items-center gap-1 text-sm"><span className="mr-1 text-xs text-muted">Rests on</span><SourceList sources={[...d.sources, ...(d.decision.sources ?? [])]} /></div>
        )}
        {toFill.some((f) => f.required) && <p className="text-xs text-waiting">The engine needs from you before it can run this: {toFill.filter((f) => f.required).map((f) => f.field).join(", ")}.</p>}
        <p className="break-all font-mono text-xs text-muted">{d.command} {JSON.stringify(d.payload)}</p>
        {note && <p role="status" className="text-sm text-waiting">{note}</p>}

        {mode === null && (
          <div className="flex flex-wrap gap-2 border-t border-line pt-3">
            <Button kind="primary" onClick={() => void accept()} disabled={!!accBlocked} title={accBlocked ?? undefined}>Accept{k("a")}</Button>
            <Button onClick={() => setMode("edit")} disabled={!!accBlocked} title={accBlocked ?? undefined}>Edit then accept{k("e")}</Button>
            <Button onClick={() => setMode("reject")} disabled={!!rejBlocked} title={rejBlocked ?? undefined}>Reject with reason{k("r")}</Button>
            {(gui || keys) && <Button onClick={() => setMode("guidance")} disabled={!!guiBlocked} title={guiBlocked ?? undefined}>Reply with guidance{k("g")}</Button>}
            {onUnsnooze && snoozedView ? <Button kind="ghost" onClick={onUnsnooze}>Wake now</Button> : onSnooze ? <Button kind="ghost" onClick={onSnooze}>Snooze 1 day{k("s")}</Button> : null}
          </div>
        )}
        {((mode === "edit" && accBlocked) || (mode === "reject" && rejBlocked) || (mode === "guidance" && guiBlocked)) && (
          <div className="space-y-2"><ErrorState error={(mode === "edit" ? accBlocked : mode === "reject" ? rejBlocked : guiBlocked)!} /><Button onClick={() => setMode(null)}>Back</Button></div>
        )}
        {mode === "edit" && acc && !accBlocked && (
          <Verb title="Edit, then accept">
            <SchemaForm spec={catalogue.get(acc.command)!} fixed={fixedOf(acc.payload)} initial={acc.payload} toFill={toFill} personName={me.personName} submitLabel="Accept with my edits"
              onCancel={() => setMode(null)} onSubmit={async (p) => { await send(acc.command, p, `Accept (edited): ${d.what}`, `${key}:edit`); }} />
          </Verb>
        )}
        {mode === "reject" && rej && !rejBlocked && (
          <Verb title="Reject with reason">
            {fb && (
              <fieldset className="mb-3 space-y-1.5 rounded border border-agent-line p-2 text-sm">
                <legend className="px-1 text-xs text-agent">Feedback to the agent</legend>
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="h-4 w-4" checked={feedback.on} disabled={!!fbBlocked} onChange={(e) => setFeedback((f) => ({ ...f, on: e.target.checked }))} />
                  Also record my reason as feedback on this proposal (feedback.add)
                </label>
                {fbBlocked ? <p className="text-xs text-muted">{fbBlocked}</p> : feedback.on && (
                  <label className="flex items-center gap-2 text-xs">Rating
                    <select className="rounded border border-line bg-paper px-1 py-0.5" value={feedback.rating} onChange={(e) => setFeedback((f) => ({ ...f, rating: e.target.value as "needs_work" | "wrong" }))}>
                      <option value="wrong">wrong</option><option value="needs_work">needs work</option>
                    </select>
                  </label>
                )}
              </fieldset>
            )}
            <ReasonForm command={rej.command} fixed={rej.payload} field={rej.reasonField} label="Reason (your own words)" minLength={feedback.on && fb && !fbBlocked ? 10 : 3}
              onCancel={() => setMode(null)} onSubmit={async (p) => {
                const row = await send(rej.command, p, `Reject: ${d.what}`);
                if (row && feedback.on && fb && !fbBlocked) {
                  await request({ command: "feedback.add", engagementId: d.engagementId, payload: { ...fb, rating: feedback.rating, comment: String(p[rej.reasonField] ?? "") }, what: `Feedback: ${d.what}`, idempotencyKey: `${key}:feedback` });
                }
              }} />
          </Verb>
        )}
        {mode === "guidance" && gui && !guiBlocked && (
          <Verb title="Reply with guidance">
            <ReasonForm command={gui.command} fixed={gui.payload} field={gui.textField} label="Guidance for the agent (your own words)"
              onCancel={() => setMode(null)} onSubmit={async (p) => { await send(gui.command, p, `Guidance: ${d.what}`); }} />
          </Verb>
        )}
      </div>
    </Card>
  );
}

const Verb = ({ title, children }: { title: string; children: ReactNode }) => (
  <section aria-label={title} className="rounded-md border border-line bg-sunken p-3"><h3 className="mb-2 text-sm font-semibold">{title}</h3>{children}</section>
);

/** A form whose one required field is the person's words; other fields come fixed from the item. */
function ReasonForm({ command, fixed, field, label, onSubmit, onCancel, minLength = 3 }: {
  command: string; fixed: Record<string, unknown>; field: string; label: string; onSubmit: (p: Record<string, unknown>) => void | Promise<void>; onCancel: () => void; minLength?: number;
}) {
  const { catalogue, me } = useApp();
  const spec = catalogue.get(command)!;
  const props = spec.inputSchema.properties ?? {};
  // The reason is always required here, even if the command's schema makes it optional: a rejection without a reason is not accepted.
  const strict = { ...spec, inputSchema: { ...spec.inputSchema, required: [...new Set([...(spec.inputSchema.required ?? []), field])],
    properties: { ...props, [field]: { ...(props[field] ?? { type: "string" }), anyOf: undefined, default: undefined, type: "string" as const, title: label, minLength: Math.max(minLength, props[field]?.minLength ?? 0), "x-widget": "textarea" as const } } } };
  const hide = Object.keys(props).filter((k) => k !== field && !(k in fixed) && !(spec.inputSchema.required ?? []).includes(k));
  return <SchemaForm spec={strict} fixed={fixed} hideFields={hide} personName={me.personName} onCancel={onCancel} onSubmit={onSubmit} submitLabel="Send to the engine" />;
}
